import * as React from "react";
import formatDate from "dateformat";
import { transparentize } from "polished";
import {
  LineChart,
  Tooltip,
  Legend,
  Line,
  ResponsiveContainer,
  YAxis,
  XAxis,
} from "recharts";
import { Colors } from "../../../../design-tokens/colors";
import { TooltipContainerStyles } from "../../../../design-tokens/charts";
import styles from "./infection-rate-chart.styles";

interface DataPoint {
  date: string;
  value: number;
}

interface InfectionRateChartsProps {
  country: string;
  benchmarkCountryName?: string;
  data: DataPoint[];
  benchmarkData?: DataPoint[] | null;
}

const RenderCustomizedDot = ({ cx, cy, index, value, length, color }: any) => {
  return index === length - 1 && value ? (
    <circle
      key={`cell-${index}`}
      cx={cx}
      cy={cy}
      r={8}
      fill={color || (value > 1 ? Colors.Pink : Colors.Green)}
    />
  ) : null;
};

const RenderCustomizedActiveDot = ({ cx, cy, index, value, color }: any) => {
  return (
    <circle
      key={`cell-${index}`}
      cx={cx}
      cy={cy}
      r={4}
      fill={color || (value > 1 ? Colors.Pink : Colors.Green)}
    />
  );
};

const InfectionRateChart: React.FC<InfectionRateChartsProps> = ({
  data,
  benchmarkData,
  country,
  benchmarkCountryName,
}) => {
  const hasBenchmark = !!benchmarkCountryName && !!benchmarkData;

  let highest = -100,
    lowest = 100;

  const mergedData = data.map((d, i: number) => {
    if (d.value > highest) {
      highest = d.value;
    }
    if (d.value < lowest) {
      lowest = d.value;
    }

    return {
      ...d,
      benchmarkValue: benchmarkData?.find((bd) => bd.date === d.date)?.value,
      scale: i === data.length - 1 ? 1 : 0,
    };
  });

  const positionOfOne = ((highest - 1) / (highest - lowest)) * 100;

  const colorKey = positionOfOne.toString().replace(".", "");

  return (
    <div css={[styles.container, hasBenchmark && styles.containerBig]}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={mergedData}
          margin={{ right: 20, top: 20, bottom: 20 }}
        >
          <defs>
            <linearGradient
              id={`infectionRateGradient_${colorKey}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0%" stopColor={Colors.Pink} />
              <stop offset={`${positionOfOne}%`} stopColor={Colors.Pink} />
              <stop offset={`${positionOfOne}%`} stopColor={Colors.Green} />
              <stop offset="100%" stopColor={Colors.Green} />
            </linearGradient>
          </defs>
          <YAxis
            yAxisId="infectionRate"
            hide={true}
            domain={[`auto`, `auto`]}
          />
          <XAxis hide={true} dataKey="date" />

          <Line
            type="monotone"
            dataKey="value"
            yAxisId="infectionRate"
            name={country}
            stroke={`url(#infectionRateGradient_${colorKey})`}
            strokeWidth={3}
            dot={(props) => (
              <RenderCustomizedDot {...props} length={data.length} />
            )}
            activeDot={<RenderCustomizedActiveDot />}
          />

          {hasBenchmark && (
            <Line
              type="monotone"
              dataKey="benchmarkValue"
              yAxisId="infectionRate"
              name={benchmarkCountryName}
              stroke={transparentize(0.3, Colors.LightBlue)}
              strokeWidth={3}
              strokeDasharray="2 3"
              dot={(props) => (
                <RenderCustomizedDot
                  {...props}
                  length={data.length}
                  color={transparentize(0.3, Colors.LightBlue)}
                />
              )}
              activeDot={(props) => (
                <RenderCustomizedActiveDot
                  {...props}
                  color={transparentize(0.3, Colors.LightBlue)}
                />
              )}
            />
          )}
          <Tooltip
            labelFormatter={(date) => formatDate(date, "mmm d")}
            cursor={false}
            contentStyle={TooltipContainerStyles}
            itemStyle={{ paddingBottom: 0 }}
          />

          {hasBenchmark && (
            <Legend
              wrapperStyle={{
                paddingTop: 20,
              }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default InfectionRateChart;
